import React, { useEffect, useMemo, useState } from 'react';
import { Text, Dropdown, Option, Badge, Link } from "@fluentui/react-components";
import { AddRegular, EditRegular, DeleteRegular, ArrowRightRegular } from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";

type AuditAction = 'INSERT' | 'UPDATE' | 'DELETE';
type AuditLog = {
  id: number;
  entityName: string;
  entityId: string | number;
  action: AuditAction;
  changes?: Record<string, { old: any; new: any }>;
  createdAt: string; // ISO
  userId?: number | null;
};

const actionStyle = {
  INSERT: { Icon: AddRegular, color: '#1e8e3e', label: 'Erstellt', badge: 'success' as const },
  UPDATE: { Icon: EditRegular, color: '#2563eb', label: 'Geändert', badge: 'brand' as const },
  DELETE: { Icon: DeleteRegular, color: '#dc2626', label: 'Gelöscht', badge: 'danger' as const },
} as const;

export function TimelineView(props: { entityName?: string; entityId?: string | number }) {
  const [items, setItems] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<AuditAction | 'ALL'>('ALL');
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const qs = new URLSearchParams();
      if (props.entityName) qs.set('entityName', String(props.entityName));
      if (props.entityId) qs.set('entityId', String(props.entityId));
      qs.set('limit', '100');

      const url = apiUrl("timeline", {
        ...(props.entityName ? { entityName: String(props.entityName) } : {}),
        ...(props.entityId ? { entityId: String(props.entityId) } : {}),
        limit: 100,
      });
      try {
        const res = await fetch(url, { headers: { Accept: 'application/json' } });
        if (!res.ok) {
          console.error("Timeline fetch failed", res.status, url);
          setItems([]);
        } else {
          const raw = (await res.json()) as any[];
          const data: AuditLog[] = (raw ?? []).map(r => ({
            id: r.id,
            entityName: r.entityName,
            entityId: r.entityId,
            action: r.action,
            changes: r.changes,
            createdAt: r.createdAt,
            userId: r.userId ?? null,
          }));
          setItems(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        console.error("Timeline fetch error", e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [props.entityName, props.entityId]);

  const filtered = useMemo(
    () => items.filter(i => (filter === 'ALL' ? true : i.action === filter)),
    [items, filter]
  );

  // Gruppiere nach Datum (Tag)
  const groupedByDay = useMemo(() => {
    const groups = new Map<string, AuditLog[]>();
    filtered
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .forEach(item => {
        const key = formatDayKey(item.createdAt);
        const arr = groups.get(key) || [];
        arr.push(item);
        groups.set(key, arr);
      });
    return Array.from(groups.entries()); // [ [dayTitle, items[]], ... ]
  }, [filtered]);

  const filterText = filter === 'ALL' ? 'Alle' : actionStyle[filter].label;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text style={{ fontSize: 22, fontWeight: 700 }}>Timeline</Text>
        <Dropdown
          aria-label="Aktion filtern"
          value={filterText}
          onOptionSelect={(_, data) => {
            const v = data.optionValue as 'ALL' | AuditAction | undefined;
            if (v) setFilter(v);
          }}
          appearance="underline"
          style={{ minWidth: 180 }}
        >
          <Option value="ALL">Alle</Option>
          <Option value="INSERT">Erstellt</Option>
          <Option value="UPDATE">Geändert</Option>
          <Option value="DELETE">Gelöscht</Option>
        </Dropdown>
      </div>

      {loading && <Text>Lade…</Text>}
      {!loading && filtered.length === 0 && <Text style={{ color: '#6b7280' }}>Keine Einträge</Text>}

      {/* Abschnitte pro Tag */}
      {!loading &&
        groupedByDay.map(([dayTitle, dayItems]) => (
          <section key={dayTitle} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Datum-Header wie in TimelineHistory */}
            <Text style={{ fontSize: 20, fontWeight: 700, marginTop: 8 }}>{dayTitle}</Text>

            {/* Timeline-Linie für diesen Tag */}
            <div style={{ position: 'relative', paddingLeft: 40 }}>
              <div style={{ position: 'absolute', left: 20, top: 0, bottom: 0, width: 2, background: '#e5e7eb' }} />

              {dayItems.map(item => {
                const a = actionStyle[item.action];
                const AIcon = a.Icon;
                const time = new Date(item.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', hour12: false });
                const isOpen = !!openIds[item.id];

                return (
                  <div key={item.id} style={{ position: 'relative', marginBottom: 14 }}>
                    {/* Größerer Aktions-Kreis am Anfang */}
                    <div
                      style={{
                        position: 'absolute',
                        left: 6,
                        top: 10,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: '#fff',
                        border: `3px solid ${a.color}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                    >
                      <AIcon style={{ fontSize: 16, color: a.color }} />
                    </div>

                    {/* Karte */}
                    <div
                      style={{
                        marginLeft: 44,
                        background: '#ffffff',
                        border: '1px solid #eef2f7',
                        boxShadow: '0 2px 8px rgba(16,24,40,0.05)',
                        borderRadius: 10,
                        padding: 12,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                      }}
                    >
                      {/* Titelzeile: Entity + Badge, Zeit rechts */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Badge appearance="outline" color={a.badge}>
                            {a.label}
                          </Badge>
                          <Text style={{ fontWeight: 600 }}>
                            {item.entityName} #{item.entityId}
                          </Text>
                        </div>
                        <Text style={{ fontSize: 12, color: '#6b7280' }}>{time}</Text>
                      </div>

                      {/* Einklappbarer Bereich für Changes */}
                      {item.changes && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Text style={{ fontSize: 13, color: '#64748b' }}>
                            {Object.keys(item.changes).length} Änderung(en)
                          </Text>
                          <button
                            type="button"
                            onClick={() => setOpenIds(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                            style={{
                              border: '1px solid #e2e8f0',
                              background: '#f8fafc',
                              color: '#0f172a',
                              borderRadius: 6,
                              padding: '6px 10px',
                              fontSize: 12,
                              cursor: 'pointer',
                            }}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? 'Details ausblenden' : 'Details anzeigen'}
                          </button>
                        </div>
                      )}

                      {item.changes && isOpen && <DiffList changes={item.changes} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
    </div>
  );
}

// Diff-Liste bleibt, wirkt aber kompakt und modern
function DiffList({ changes }: { changes: Record<string, { old: any; new: any }> }) {
  const entries = Object.entries(changes);
  if (!entries.length) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6 }}>
      {entries.map(([field, { old, new: nw }]) => (
        <div
          key={field}
          style={{
            display: 'grid',
            gridTemplateColumns: '220px 1fr 24px 1fr',
            alignItems: 'start',
            gap: 8,
          }}
        >
          <Text style={{ fontWeight: 600, color: '#0f172a' }}>{field}</Text>
          <ValuePill value={old} tone="muted" />
          <ArrowRightRegular style={{ color: '#64748b', marginTop: 2 }} />
          <ValuePill value={nw} tone="accent" />
        </div>
      ))}
    </div>
  );
}

function ValuePill({ value, tone }: { value: any; tone: 'muted' | 'accent' }) {
  const isLinkLike = typeof value === 'string' && value.length > 0 && !value.includes(' ') && /[A-Za-z]/.test(value);
  const color = tone === 'accent' ? '#0ea5e9' : '#475569';
  const bg = tone === 'accent' ? '#e0f2fe' : '#f1f5f9';
  const text = formatDisplay(value);

  const content = isLinkLike ? (
    <Link appearance="subtle" href="#" onClick={e => e.preventDefault()} style={{ color }}>
      {text}
    </Link>
  ) : (
    <span style={{ color }}>{text}</span>
  );

  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${tone === 'accent' ? '#bae6fd' : '#e2e8f0'}`,
        borderRadius: 8,
        padding: '6px 10px',
        fontSize: 13,
        lineHeight: 1.4,
        wordBreak: 'break-word',
      }}
      title={fullString(value)}
    >
      {content}
    </div>
  );
}

function formatDisplay(v: any) {
  if (v === null || v === undefined || v === "") return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    try {
      const d = new Date(v);
      return d.toLocaleString();
    } catch {
      return v;
    }
  }
  if (typeof v === 'string' && v.length <= 40) return v;
  if (typeof v === 'string') return v.slice(0, 80) + '…';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 80) + '…' : s;
  }
  return String(v);
}
function fullString(v: any) {
  try {
    if (v === null || v === undefined) return '—';
    return typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v);
  } catch {
    return String(v);
  }
}

function formatDayKey(iso: string) {
  const d = new Date(iso);
  const fmt = d.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return fmt; // z. B. "Montag, 10. November 2025"
}